import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from './gsap/SplitText.js';

//!ANIMATIONS

function lazyAnimation(element, callback, threshold = 500) {
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    callback(entry.isIntersecting);
                });
            },
            { rootMargin: `0px 0px ${threshold}px 0px` }
        );

        io.observe(element);
    } else {
        callback(true);
    }
}

gsap.registerPlugin(ScrollTrigger);

theme.animations = [
    {
        selector: '[anim-header]',
        type: 'text',
        animation: (target) =>
            gsap.fromTo(
                target,
                { opacity: 0, y: 20 },
                { duration: 1, opacity: 1, y: 0, stagger: 0.1, ease: 'power3.out' }
            )
    },
    {
        selector: '[anim-header-right]',
        type: 'text',
        animation: (target) =>
            gsap.fromTo(
                target,
                { opacity: 0, x: 30 },
                { duration: 1, opacity: 1, x: 0, stagger: 0.2, ease: 'expo.out' }
            )
    },
    {
        selector: '[anim-sub-header]',
        type: 'text',
        animation: (target) =>
            gsap.fromTo(
                target,
                { opacity: 0, y: -20 },
                { duration: 1, opacity: 1, y: 0, stagger: 0.1, ease: 'power3.out' }
            )
    },
    {
        selector: '[anim-text]',
        type: 'text',
        animation: (target) =>
            gsap.fromTo(
                target,
                { opacity: 0, y: 20 },
                { duration: 1, y: 0, opacity: 1, ease: 'expo.out', stagger: 0.1 }
            )
    },
    {
        selector: '[anim-text-instant]',
        instant: true,
        type: 'text',
        animation: (target) =>
            gsap.fromTo(
                target,
                { opacity: 0, y: 20 },
                { duration: 1, y: 0, opacity: 1, ease: 'expo.out', stagger: 0.1 }
            )
    },
    {
        selector: '.line-horisontal',
        type: 'element',
        initial: 'width',
        animation: (target) =>
            gsap.fromTo(target, { width: 0 }, { duration: 3, width: '100%', ease: 'power3.out' })
    },
    {
        selector: '.line-vertical',
        type: 'element',
        initial: 'height',
        animation: (target) =>
            gsap.fromTo(target, { height: 0 }, { duration: 3, height: '100%', ease: 'power3.out' })
    }
];

function getAnimationTarget(config, element) {
    if (config.type === 'text') {
        const text = new SplitText(element, { type: 'words,chars,lines' });
        const textTarget = 'lines' || config.textTarget;

        config.splitTextInstance = text;
        return text[textTarget];
    }
    return element;
}

function initializeAnimation(config) {
    const elements = gsap.utils.toArray(config.selector);

    elements.forEach((element) => {
        const animationTarget = getAnimationTarget(config, element);

        if (config.type === 'text') {
            gsap.set(animationTarget, { opacity: 0 });
        } else {
            if (config.initial === 'width') {
                gsap.set(animationTarget, { width: 0 });
            }
            if (config.initial === 'height') {
                gsap.set(animationTarget, { height: 0 });
            }
        }

        if (config.instant === true) {
            config.animation(animationTarget);
        } else {
            ScrollTrigger.create({
                trigger: element,
                start: 'top 80%',
                onEnter: () => config.animation(animationTarget),
                once: true
            });
        }
    });
}

function initializeAnimations(animations) {
    if (userPrefersReducedMotion()) return;

    animations.forEach((config) => {
        const elements = document.querySelectorAll(config.selector);
        elements.forEach((element) => initializeAnimation(config));
    });
}

function userPrefersReducedMotion() {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    return query.matches;
}

document.addEventListener('DOMContentLoaded', () => initializeAnimations(theme.animations));

/*
This helper function makes a group of elements animate along the x-axis in a seamless, responsive loop.

Features:
- Uses xPercent so that even if the widths change (like if the window gets resized), it should still work in most cases.
- When each item animates to the left or right enough, it will loop back to the other side
- Optionally pass in a config object with values like "speed" (default: 1, which travels at roughly 100 pixels per second), paused (boolean),  repeat, reversed, and paddingRight.
- The returned timeline will have the following methods added to it:
- next() - animates to the next element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
- previous() - animates to the previous element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
- toIndex() - pass in a zero-6d index value of the element that it should animate to, and optionally pass in a vars object to control duration, easing, etc. Always goes in the shortest direction
- current() - returns the current index (if an animation is in-progress, it reflects the final index)
- times - an Array of the times on the timeline where each element hits the "starting" spot. There's also a label added accordingly, so "label1" is when the 2nd element reaches the start.
*/
function horizontalLoop(items, config) {
    items = gsap.utils.toArray(items);
    config = config || {};
    let tl = gsap.timeline({
            repeat: config.repeat,
            paused: config.paused,
            defaults: { ease: 'none' },
            onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)
        }),
        length = items.length,
        startX = items[0].offsetLeft,
        times = [],
        widths = [],
        xPercents = [],
        curIndex = 0,
        pixelsPerSecond = (config.speed || 1) * 100,
        snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
        totalWidth,
        curX,
        distanceToStart,
        distanceToLoop,
        item,
        i;
    gsap.set(items, {
        // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
        xPercent: (i, el) => {
            let w = (widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px')));
            xPercents[i] = snap(
                (parseFloat(gsap.getProperty(el, 'x', 'px')) / w) * 100 + gsap.getProperty(el, 'xPercent')
            );
            return xPercents[i];
        }
    });
    gsap.set(items, { x: 0 });
    totalWidth =
        items[length - 1].offsetLeft +
        (xPercents[length - 1] / 100) * widths[length - 1] -
        startX +
        items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], 'scaleX') +
        (parseFloat(config.paddingRight) || 0);
    for (i = 0; i < length; i++) {
        item = items[i];
        curX = (xPercents[i] / 100) * widths[i];
        distanceToStart = item.offsetLeft + curX - startX;
        distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX');
        tl.to(
            item,
            {
                xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
                duration: distanceToLoop / pixelsPerSecond
            },
            0
        )
            .fromTo(
                item,
                {
                    xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100)
                },
                {
                    xPercent: xPercents[i],
                    duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                    immediateRender: false
                },
                distanceToLoop / pixelsPerSecond
            )
            .add('label' + i, distanceToStart / pixelsPerSecond);
        times[i] = distanceToStart / pixelsPerSecond;
    }
    function toIndex(index, vars) {
        vars = vars || {};
        Math.abs(index - curIndex) > length / 2 && (index += index > curIndex ? -length : length); // always go in the shortest direction
        let newIndex = gsap.utils.wrap(0, length, index),
            time = times[newIndex];
        if (time > tl.time() !== index > curIndex) {
            // if we're wrapping the timeline's playhead, make the proper adjustments
            vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
            time += tl.duration() * (index > curIndex ? 1 : -1);
        }
        curIndex = newIndex;
        vars.overwrite = true;
        return tl.tweenTo(time, vars);
    }
    tl.next = (vars) => toIndex(curIndex + 1, vars);
    tl.previous = (vars) => toIndex(curIndex - 1, vars);
    tl.current = () => curIndex;
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true); // pre-render for performance
    if (config.reversed) {
        tl.vars.onReverseComplete();
        tl.reverse();
    }
    return tl;
}

function duplicateChildNodes(element) {
    const children = element.childNodes;
    const fragment = document.createDocumentFragment();

    children.forEach((child) => {
        const clone = child.cloneNode(true);
        fragment.appendChild(clone);
    });

    element.appendChild(fragment);
}

window.lazyAnimation = lazyAnimation;
window.horizontalLoop = horizontalLoop;
window.duplicateChildNodes = duplicateChildNodes;

window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
window.SplitText = SplitText;
